import { ExecuteActionRequest, GameState } from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";

/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to execute
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }

    /**
     * Get the inventory for the current player from the server application
     *
     * @returns A list of inventory items
     */
    public async getInventory(): Promise<{ items: { name: string; images: string[] }[] }> {
        return this.getJsonApi<{ items: { name: string; images: string[] }[] }>("game/inventory");
    }

    /**
     * Get the leaderboard for the current player session from the server application
     *
     * @returns A list of inventory items
     */
    public async getLeaderboard(): Promise<{ username: string; duration_seconds: number }[]> {
        return this.getJsonApi<{ username: string; duration_seconds: number }[]>("game/leaderboard/getHighscores");
    }

    // Ensure this method is correctly fetching data
    public async getRooms(): Promise<{ rooms: Array<{ name: string; images: string[]; position: { x: number; y: number }; visited: boolean; alias: string }> }> {
        try {
            const rooms: {
                rooms: Array<{
                    name: string;
                    images: string[];
                    position: {
                        x: number;
                        y: number;
                    };
                    visited: boolean;
                    alias: string;
                }>;
            } = await this.getJsonApi<{ rooms: Array<{ name: string; images: string[]; position: { x: number; y: number }; visited: boolean; alias: string }> }>("game/rooms");
            console.log("Rooms fetched:", rooms);
            return rooms;
        }
        catch (error) {
            console.error("Error fetching rooms:", error);
            return { rooms: [] }; // Return an empty array on error
        }
    }

    public async moveRoom(newRoom: string): Promise<void> {
        try {
            // Send a request to the server to update the current room
            await this.postJsonApi("game/move-room", { newRoom });

            // After the room is moved, you may want to fetch the updated game state or rooms
            // This is optional, depending on how your state needs to be handled
            const updatedGameState: GameState = await this.getGameState();
            console.log("Updated Game State:", updatedGameState);
        }
        catch (error) {
            console.error("Error moving room:", error);
        }
    }

    public async getHighScores(): Promise<{ scores: { player: string; time: number }[] }> {
        return this.getJsonApi<{ scores: { player: string; time: number }[] }>("game/highscores");
    }

    /**
     * Submit a high score to the server
     *
     * @param player The player's name
     * @param time The player's time
     *
     * @returns A promise that resolves once the high score is successfully submitted
     */
    // In GameRouteService.ts
    public async submitHighScore(playerName: string, timeString: number): Promise<void> {
        try {
            await this.postJsonApi("game/highscore", { player: playerName, time: timeString });
            console.log("High score submitted successfully");
        }
        catch (error) {
            console.error("Error submitting high score:", error);
        }
    }

    public async submitDuration(duration_seconds: number, username: string): Promise<void> {
        try {
            await this.postJsonApi("game/duration", {
                duration_seconds,
                username,
            });
            console.log("✅ Duration submitted:", duration_seconds, username);
        }
        catch (error) {
            console.error("❌ Failed to submit duration:", error);
        }
    }

    public async getSoundSettings(): Promise<{ volume: number; muted: boolean }> {
        return this.getJsonApi<{ volume: number; muted: boolean }>("game/soundsettings");
    }

    public async setSoundSettings(volume: number, muted: boolean): Promise<void> {
        await this.postJsonApi("game/soundsettings", { volume, muted });
    }

    public async getInventoryCount(): Promise<number> {
        return this.getJsonApi<number>("game/accusation");
    }
}
